import { NavLink } from 'react-router';
import { ThemeToggle } from './ThemeToggle';
import { useMyInvitations } from '../library/invitationQueries';

/**
 * The far-left rail: which part of the drawer you are in.
 *
 * It also owns the theme toggle and sign out. Those used to live in the library sidebar, but the
 * canvas route is edge-to-edge and has no sidebar — leaving them there would mean opening a
 * diagram was a way to lose the ability to sign out.
 */
export function SectionRail({ displayName, onSignOut }: { displayName: string; onSignOut: () => void }) {
  return (
    <nav
      aria-label="Sections"
      className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-border-subtle
        bg-surface-sunken py-3"
    >
      <span
        aria-hidden="true"
        title="Script Drawer"
        className="mb-2 flex size-8 items-center justify-center rounded-md bg-accent text-sm
          font-semibold text-accent-contrast"
      >
        SD
      </span>

      <RailLink to="/" label="Scripts" icon={<ScriptsIcon />} />
      <RailLink to="/diagrams" label="Diagrams" icon={<DiagramsIcon />} />

      <InvitationsBadge />

      <div className="mt-auto flex flex-col items-center gap-1">
        <ThemeToggle />
        <button
          type="button"
          onClick={onSignOut}
          aria-label={`Sign out of ${displayName}`}
          title={`Sign out of ${displayName}`}
          className="rounded-md p-2 text-text-muted transition-colors hover:bg-surface-raised
            hover:text-text"
        >
          <span aria-hidden="true" className="text-base leading-none">
            ⏻
          </span>
        </button>
      </div>
    </nav>
  );
}

interface RailLinkProps {
  to: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * `end` on the Scripts link matters: without it "/" matches every route and both icons look
 * active at once.
 */
function RailLink({ to, label, icon }: RailLinkProps) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      aria-label={label}
      title={label}
      className={({ isActive }) =>
        `relative flex size-10 items-center justify-center rounded-md transition-colors
         ${isActive ? 'bg-accent-subtle text-text' : 'text-text-muted hover:bg-surface-raised hover:text-text'}`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute top-1.5 -left-2 h-7 w-0.5 rounded-full bg-accent"
            />
          )}
          {icon}
        </>
      )}
    </NavLink>
  );
}

/**
 * A dot on the rail when something is waiting. The sidebar's fuller notice is still there; this
 * one exists because the canvas route has no sidebar to carry it.
 */
function InvitationsBadge() {
  const invitations = useMyInvitations();
  const waiting = invitations.data?.length ?? 0;

  if (waiting === 0) {
    return null;
  }

  return (
    <NavLink
      to="/invitations"
      aria-label={waiting === 1 ? '1 invitation waiting' : `${waiting} invitations waiting`}
      title={waiting === 1 ? '1 invitation waiting' : `${waiting} invitations waiting`}
      className="flex size-10 items-center justify-center rounded-md text-text-muted
        transition-colors hover:bg-surface-raised hover:text-text"
    >
      <span className="relative flex size-6 items-center justify-center">
        <span aria-hidden="true" className="text-base leading-none">
          ✉
        </span>
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-accent"
        />
      </span>
    </NavLink>
  );
}

function ScriptsIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-5"
    >
      <path d="M9 8 5.5 12 9 16" />
      <path d="m15 8 3.5 4-3.5 4" />
    </svg>
  );
}

function DiagramsIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-5"
    >
      <rect x="9" y="3" width="6" height="4.5" rx="1" />
      <rect x="3" y="16.5" width="6" height="4.5" rx="1" />
      <rect x="15" y="16.5" width="6" height="4.5" rx="1" />
      <path d="M12 7.5v4.75M6 16.5v-2.5h12v2.5" />
    </svg>
  );
}
